import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Globe, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" />
            Retour
          </Link>
          <div className="flex items-center gap-2">
            <img src="/xyron-logo.png" alt="Xyron Core ERP" className="w-8 h-8 rounded-lg" />
            <span className="font-bold">Xyron Core ERP</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Politique de Confidentialité</h1>
          <p className="text-slate-500">Conforme à la Loi fédérale sur la protection des données (LPD)</p>
        </div>

        <div className="space-y-8">
          {/* Introduction */}
          <Card>
            <CardHeader>
              <CardTitle>Introduction</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>
                Xyron Core ERP s'engage à protéger la vie privée de ses utilisateurs conformément à la 
                <strong> Loi fédérale sur la protection des données (LPD)</strong> du 25 septembre 2020 
                et à son ordonnance d'application (OPDo).
              </p>
              <p>
                Cette politique de confidentialité décrit les données personnelles que nous collectons, 
                la manière dont nous les utilisons et les droits dont vous disposez.
              </p>
            </CardContent>
          </Card>

          {/* Responsable du traitement */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-blue-600" />
                Responsable du traitement
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>
                <strong>Xyron Core ERP</strong><br />
                Suisse<br />
                Contact : Creez un compte d'essai gratuit pour acceder a notre systeme de support interne.
              </p>
            </CardContent>
          </Card>

          {/* Données collectées */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-600" />
                Données personnelles collectées
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>Nous collectons les données suivantes :</p>
              
              <h4 className="font-semibold mt-4">Données d'identification</h4>
              <ul>
                <li>Nom et prénom</li>
                <li>Adresse email</li>
                <li>Nom de l'entreprise</li>
                <li>Adresse postale</li>
                <li>Numéro de téléphone (optionnel)</li>
              </ul>
              
              <h4 className="font-semibold mt-4">Données professionnelles</h4>
              <ul>
                <li>Numéro IDE/CHE (Registre du commerce suisse)</li>
                <li>Numéro de TVA</li>
                <li>IBAN bancaire</li>
                <li>Informations sur vos clients et factures</li>
              </ul>
              
              <h4 className="font-semibold mt-4">Données techniques</h4>
              <ul>
                <li>Adresse IP</li>
                <li>Type de navigateur</li>
                <li>Données de connexion (logs)</li>
              </ul>
            </CardContent>
          </Card>

          {/* Finalités */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-amber-600" />
                Finalités du traitement
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>Vos données sont utilisées pour :</p>
              <ul>
                <li><strong>Fourniture du service</strong> : création de factures, devis, gestion clients</li>
                <li><strong>Gestion du compte</strong> : authentification, abonnements, facturation</li>
                <li><strong>Communication</strong> : notifications, rappels, support technique</li>
                <li><strong>Amélioration du service</strong> : statistiques anonymisées, correction de bugs</li>
                <li><strong>Obligations légales</strong> : conformité fiscale, réponse aux autorités</li>
              </ul>
              <p>
                <strong>Base juridique :</strong> Exécution du contrat (art. 6 al. 1 let. b RGPD / art. 31 LPD), 
                intérêts légitimes (art. 6 al. 1 let. f RGPD), obligations légales.
              </p>
            </CardContent>
          </Card>

          {/* Durée de conservation */}
          <Card>
            <CardHeader>
              <CardTitle>Durée de conservation</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <ul>
                <li><strong>Données de compte</strong> : conservées pendant la durée de votre abonnement + 30 jours après résiliation</li>
                <li><strong>Factures et documents comptables</strong> : 10 ans (obligation légale suisse, art. 958f CO)</li>
                <li><strong>Logs techniques</strong> : 12 mois maximum</li>
                <li><strong>Données de paiement</strong> : traitées par notre prestataire Stripe, selon leur politique</li>
              </ul>
            </CardContent>
          </Card>

          {/* Partage des données */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-cyan-600" />
                Partage et transfert des données
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>Vos données peuvent être partagées avec :</p>
              <ul>
                <li><strong>Stripe</strong> : traitement des paiements (certifié PCI-DSS)</li>
                <li><strong>Fournisseur d'hébergement</strong> : stockage sécurisé des données</li>
                <li><strong>Services d'email</strong> : envoi de notifications (votre propre SMTP si configuré)</li>
              </ul>
              <p>
                <strong>Transferts hors Suisse :</strong> En cas de transfert vers des pays ne disposant pas 
                d'un niveau de protection adéquat, nous appliquons des clauses contractuelles types 
                approuvées par le PFPDT (Préposé fédéral à la protection des données).
              </p>
              <p>
                Nous ne vendons jamais vos données personnelles à des tiers.
              </p>
            </CardContent>
          </Card>

          {/* Sécurité */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-red-600" />
                Sécurité des données
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>Nous mettons en œuvre des mesures de sécurité appropriées :</p>
              <ul>
                <li>Chiffrement des données en transit (HTTPS/TLS 1.3)</li>
                <li>Chiffrement des données au repos</li>
                <li>Authentification sécurisée avec hachage des mots de passe (bcrypt)</li>
                <li>Contrôle d'accès strict aux données</li>
                <li>Sauvegardes régulières</li>
                <li>Surveillance et détection des intrusions</li>
              </ul>
            </CardContent>
          </Card>

          {/* Droits */}
          <Card>
            <CardHeader>
              <CardTitle>Vos droits</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>Conformément à la LPD, vous disposez des droits suivants :</p>
              <ul>
                <li><strong>Droit d'accès</strong> : obtenir une copie de vos données personnelles</li>
                <li><strong>Droit de rectification</strong> : corriger des données inexactes</li>
                <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données (sous réserve des obligations légales)</li>
                <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré (CSV, PDF)</li>
                <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données</li>
                <li><strong>Droit de retrait du consentement</strong> : retirer votre consentement à tout moment</li>
              </ul>
              <p>
                Pour exercer ces droits, connectez-vous a votre compte et contactez-nous via le systeme de support interne (menu Support).
              </p>
              <p>
                Vous avez également le droit de déposer une plainte auprès du 
                <strong> Préposé fédéral à la protection des données et à la transparence (PFPDT)</strong>.
              </p>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card>
            <CardHeader>
              <CardTitle>Cookies</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>Notre site utilise des cookies strictement nécessaires au fonctionnement du service :</p>
              <ul>
                <li><strong>Cookie de session</strong> : maintien de votre connexion</li>
                <li><strong>Token d'authentification</strong> : sécurisation de l'accès à votre compte</li>
              </ul>
              <p>
                Nous n'utilisons pas de cookies publicitaires ou de tracking tiers.
              </p>
            </CardContent>
          </Card>

          {/* Modifications */}
          <Card>
            <CardHeader>
              <CardTitle>Modifications de cette politique</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>
                Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. 
                Les modifications entreront en vigueur dès leur publication sur cette page.
              </p>
              <p>
                En cas de modification substantielle, nous vous en informerons par email.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>
                Pour toute question concernant cette politique ou vos données personnelles :
              </p>
              <p>
                Creez un compte d'essai gratuit et contactez-nous directement via le systeme de <strong>Support interne</strong> de l'application.
              </p>
            </CardContent>
          </Card>
        </div>

        <p className="text-center text-sm text-slate-400 mt-12">
          Dernière mise à jour : Mars 2026
        </p>
      </main>
    </div>
  );
};

export default PrivacyPage;
